import {
  BriefApplicationResponseDto,
  BriefResponseDto,
} from '@/app/common/general-types/briefs.dto';
import { FilterCreatorsDto } from '@/app/common/general-types/filters.dto';
import {
  buildScreeningQaRows,
  evaluateAnswer,
} from '@/app/pages/manage-brief-tabs/tabs/assignment/screening-qa.util';

/** questionId → filter value (Yes/No/pass/fail/answered) */
export type ScreeningFilterMap = Record<string, string>;

export function hasActiveScreeningFilters(
  screening: ScreeningFilterMap | null | undefined,
): boolean {
  if (!screening) {
    return false;
  }
  return Object.values(screening).some((value) => !!value);
}

function creatorAgeYears(dateOfBirth: Date | string | undefined | null): number | null {
  if (!dateOfBirth) {
    return null;
  }
  const dob = dateOfBirth instanceof Date ? dateOfBirth : new Date(dateOfBirth);
  if (Number.isNaN(dob.getTime())) {
    return null;
  }
  const now = new Date();
  let age = now.getFullYear() - dob.getFullYear();
  const monthDelta = now.getMonth() - dob.getMonth();
  if (monthDelta < 0 || (monthDelta === 0 && now.getDate() < dob.getDate())) {
    age -= 1;
  }
  return age;
}

function creatorAverageRating(applicant: BriefApplicationResponseDto): number | null {
  const creator = applicant.creator;
  if (!creator) {
    return null;
  }
  const fromSummary = creator.reviews_summary?.average_rating;
  if (typeof fromSummary === 'number' && Number.isFinite(fromSummary) && fromSummary > 0) {
    return fromSummary;
  }
  const direct = creator['average_rating'];
  if (typeof direct === 'number' && Number.isFinite(direct) && direct > 0) {
    return direct;
  }
  return null;
}

function creatorReviewCount(applicant: BriefApplicationResponseDto): number {
  const creator = applicant.creator;
  if (!creator) {
    return 0;
  }
  const fromSummary = creator.reviews_summary?.total_reviews;
  if (typeof fromSummary === 'number' && Number.isFinite(fromSummary)) {
    return fromSummary;
  }
  const direct = creator['total_reviews'];
  if (typeof direct === 'number' && Number.isFinite(direct)) {
    return direct;
  }
  return 0;
}

function profileFiltersActive(filters: FilterCreatorsDto): boolean {
  return !!(
    filters.country?.length
    || filters.gender != null
    || filters.rating != null
    || filters.ageFrom != null
    || filters.ageTo != null
    || filters.isVerified === true
    || typeof filters.hasReviews === 'boolean'
    || filters.tikTokFollowersFrom != null
    || filters.instagramFollowersFrom != null
  );
}

function resolveScreeningRow(
  rows: ReturnType<typeof buildScreeningQaRows>,
  questionKey: string,
) {
  const byId = rows.find((r) => r.questionId === questionKey);
  if (byId) {
    return byId;
  }
  if (questionKey.startsWith('idx:')) {
    const index = Number(questionKey.slice(4));
    if (Number.isInteger(index) && index >= 0 && index < rows.length) {
      return rows[index];
    }
  }
  return undefined;
}

function matchesScreeningFilters(
  brief: BriefResponseDto | null | undefined,
  applicant: BriefApplicationResponseDto,
  screeningFilters: ScreeningFilterMap,
): boolean {
  const rows = buildScreeningQaRows(brief, applicant);

  for (const [questionId, required] of Object.entries(screeningFilters)) {
    if (!required) {
      continue;
    }
    const row = resolveScreeningRow(rows, questionId);
    if (!row) {
      return false;
    }

    if (required === 'answered') {
      if (!row.answer.trim()) {
        return false;
      }
      continue;
    }

    if (required === 'pass' || required === 'fail') {
      const verdict = evaluateAnswer(row.expected, row.answer);
      if (verdict !== required) {
        return false;
      }
      continue;
    }

    // Exact answer match (Yes/No or free-text value)
    if (row.answer.trim().toLowerCase() !== required.trim().toLowerCase()) {
      return false;
    }
  }

  return true;
}

export interface ApplicantClientFilterOptions {
  brief?: BriefResponseDto | null;
  screeningFilters?: ScreeningFilterMap;
}

/**
 * Client-side applicant filtering. Needed because the brief-application
 * filter-creators API does not reliably apply rating / verified / hasReviews
 * (and as a safety net for other profile filters / screening answers).
 */
export function filterApplicantsByProfile(
  applicants: BriefApplicationResponseDto[],
  filters: FilterCreatorsDto,
  options: ApplicantClientFilterOptions = {},
): BriefApplicationResponseDto[] {
  const screeningFilters = options.screeningFilters ?? {};
  const screeningActive = hasActiveScreeningFilters(screeningFilters);
  const profileActive = profileFiltersActive(filters);

  if (!profileActive && !screeningActive) {
    return applicants;
  }

  return applicants.filter((applicant) => {
    const creator = applicant.creator;
    if (!creator) {
      return false;
    }

    if (filters.country?.length) {
      const code = (creator.countryCode ?? '').toUpperCase();
      const allowed = filters.country.map((c) => String(c).toUpperCase());
      if (!allowed.includes(code)) {
        return false;
      }
    }

    if (filters.gender != null) {
      const wanted = Array.isArray(filters.gender)
        ? filters.gender.map(Number)
        : [Number(filters.gender)];
      if (!wanted.includes(Number(creator.gender))) {
        return false;
      }
    }

    if (filters.ageFrom != null || filters.ageTo != null) {
      const age = creatorAgeYears(creator.dateOfBirth);
      if (age == null) {
        return false;
      }
      if (filters.ageFrom != null && age < Number(filters.ageFrom)) {
        return false;
      }
      if (filters.ageTo != null && age > Number(filters.ageTo)) {
        return false;
      }
    }

    if (filters.isVerified === true && creator.isVerified !== true) {
      return false;
    }

    if (filters.hasReviews === true && creatorReviewCount(applicant) <= 0) {
      return false;
    }

    // `false` is the Assignment rating select's "New creators": nobody has reviewed them yet.
    if (filters.hasReviews === false && creatorReviewCount(applicant) > 0) {
      return false;
    }

    if (filters.rating != null) {
      const rating = creatorAverageRating(applicant);
      if (rating == null || rating < Number(filters.rating)) {
        return false;
      }
    }

    if (filters.tikTokFollowersFrom != null) {
      if ((creator.tikTokFollowers ?? 0) < Number(filters.tikTokFollowersFrom)) {
        return false;
      }
    }

    if (filters.instagramFollowersFrom != null) {
      if ((creator.instaFollowers ?? 0) < Number(filters.instagramFollowersFrom)) {
        return false;
      }
    }

    if (screeningActive && !matchesScreeningFilters(options.brief, applicant, screeningFilters)) {
      return false;
    }

    return true;
  });
}

export function hasActiveApplicantProfileFilters(
  filters: FilterCreatorsDto,
  screeningFilters?: ScreeningFilterMap,
): boolean {
  return profileFiltersActive(filters) || hasActiveScreeningFilters(screeningFilters);
}
